import { Box, Button, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../../../app/hooks";
import { anyPermission, PermissionId } from "../../../../app/permissions";
import PrizaContainer from "../../../../features/base/grid/PrizaContainer";
import { isTokenReady } from "../../../../features/user/UserAvatar";
import { LoadState, worstState } from "../../../../redux/common";
import { PresenceEnum, SubjectInfo, SubjectPerformedInfo } from "../../../../redux/curriculum/model";
import { selectUser } from "../../../../redux/user/userSlice";

import { presenceListSlice, retrievePresenceList, selectPresencetList } from "../../../../redux/curriculum/presenceListSlice";
import PresenceActionButtons from "./PresenceActionButtons";
import { performPresenceChangeClassroom as performPresenceChangeClassroom, selectPresenceChangeClassroom } from "../../../../redux/curriculum/presenceChangeClassroomSlice";

interface ContentListProps {
    subject?: SubjectInfo;
    subjectPerformed: SubjectPerformedInfo;
    back: () => void;
}

const PresenceList = ({ subject, subjectPerformed, back }: ContentListProps) => {
    const dispatch = useAppDispatch();
    const user = useSelector(selectUser);
    const presenceStudentList = useSelector(selectPresencetList);
    const presenceChangeClassroom = useSelector(selectPresenceChangeClassroom);

    const canEdit = useMemo(
        () => anyPermission(
            user.priza.info, `${subjectPerformed.schoolId}`,
            [PermissionId.SCHOOL_ADMIN, PermissionId.SCHOOL_TEACHER]
        )
            || anyPermission(
                user.priza.info, `${subjectPerformed.classroomId}`,
                [PermissionId.CLASSROOM_ADMIN, PermissionId.CLASSROOM_TEACHER]
            ),
        [user, subjectPerformed.schoolId, subjectPerformed.classroomId]
    );

    useEffect(() => {
        dispatch(presenceListSlice.actions.check({ subjectPerformedId: `${subjectPerformed.id}` }));
        if (isTokenReady(user) && presenceStudentList.state.state == LoadState.NotStarted) {
            dispatch(retrievePresenceList({ subjectPerformedId: `${subjectPerformed.id}` }));
        }
    }, [user, subjectPerformed, presenceStudentList]);

    const list = useMemo(() => {
        if (!presenceStudentList.list) {
            return [];
        }
        let list = [...presenceStudentList.list];
        list.sort((a, b) => a.student.name.localeCompare(b.student.name));
        return list;
    }, [presenceStudentList.list]);

    const buildContent = () => {
        if (list.length == 0) {
            return <Paper sx={{ p: 2, m: 2, width: "100%" }}>
                <Typography variant="h6">Nenhum aluno para a sala</Typography>
            </Paper>;
        }
        return <Box display="flex" flexDirection="column" gap={2} sx={{ p: 2, width: "100%" }}>
            <Stack direction="row" alignItems="center" justifyContent="space-evenly">
                <Button
                    onClick={() => {
                        back();
                    }}
                    variant="outlined" color="primary">
                    <Typography variant="h6">{`Voltar`}</Typography>
                </Button>

                <Button 
                    disabled={presenceChangeClassroom.state.state == LoadState.Loading || !canEdit}
                    onClick={() => {
                        dispatch(performPresenceChangeClassroom({subjectPerformedId: subjectPerformed.id, present: PresenceEnum.PRESENT}));
                    }}
                    variant="contained" color="success">
                    <Typography variant="h6">{`Todos Presentes`}</Typography>
                </Button>
                <Button 
                    disabled={presenceChangeClassroom.state.state == LoadState.Loading || !canEdit}
                    onClick={() => {
                        dispatch(performPresenceChangeClassroom({subjectPerformedId: subjectPerformed.id, present: PresenceEnum.ABSENT}));
                    }}
                    variant="contained" color="error">
                    <Typography variant="h6">{`Todos Ausentes`}</Typography>
                </Button>
            </Stack>
            <Table sx={{ minWidth: 650, width: "100%" }} size="small">
                <TableHead>
                    <TableRow>
                        {/* <TableCell align="left" sx={{
                            color: (theme) => theme.palette.primary.contrastText,
                            backgroundColor: (theme) => theme.palette.primary.main,
                        }}>Ações</TableCell> */}
                        <TableCell align="left" sx={{
                            color: (theme) => theme.palette.primary.contrastText,
                            backgroundColor: (theme) => theme.palette.primary.main,
                        }}>
                            {subjectPerformed.startedAt ? `Presença - ${new Date(subjectPerformed.startedAt).toLocaleDateString("pt-BR", {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric"
                            })}` : "Presença"}
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {list.map((presence) => {
                        return <TableRow key={presence.student.id}>
                            <TableCell align="left">
                                <Stack direction="row" alignItems="center" gap={1}>
                                    <PresenceActionButtons 
                                        subjectPerformedId={subjectPerformed.id} presence={presence} 
                                        disabled={presenceChangeClassroom.state.state == LoadState.Loading || !canEdit}
                                    />
                                    {presence.student.name}
                                </Stack>
                            </TableCell>
                        </TableRow>;
                    })}
                </TableBody>
            </Table>
        </Box>;
    };

    const buttons = canEdit ? [] : undefined;

    return <PrizaContainer
        toolbar={{
            label: "Lista de presença",
            buttons: buttons
        }}
        state={worstState([presenceStudentList.state])}
        retry={() => {
            dispatch(presenceListSlice.actions.clean());
        }}
        messages={{
            empty: "Nenhum aluno para a sala.",
            error: "Erro ao carregar alunos da sala."
        }}
        content={buildContent}
    />;
};

export default PresenceList;


